<template>
  <!-- A sideways rail of cards with the next one peeking in (S2_Rails).
       Cards you write, or products / projects / posts resolved by the server
       into `items` ({title, text, image, link, price?, wasPrice?}). -->
  <SectionBand :heading="section.heading || section.title" :variant="variant" :background="section.background" label="Cards">
    <div class="s2-rail">
      <div class="s2-rail__head" v-if="cards.length > 2">
        <button type="button" class="s2-rail__nav" aria-label="Previous" @click="step(-1)">‹</button>
        <button type="button" class="s2-rail__nav" aria-label="Next" @click="step(1)">›</button>
      </div>
      <ul ref="track" class="s2-rail__track" tabindex="0" :aria-label="`${section.heading || 'Cards'}, scroll sideways`">
        <li v-for="(c, i) in cards" :key="i" class="s2-rail__item">
          <component :is="c.href ? 'a' : 'div'" :href="c.href ? back(c.href) : null" class="s2-rail__card">
            <Media v-if="c.image" :media="c.image" ratio="4 / 5" />
            <strong class="s2-rail__title">{{ c.title }}</strong>
            <span v-if="c.price" class="t-price" :class="{'t-price--sale': c.wasPrice}">£{{ c.price }}<span v-if="c.wasPrice" class="t-price__was">£{{ c.wasPrice }}</span></span>
            <span v-else-if="c.text" class="t-muted s2-rail__text">{{ c.text }}</span>
          </component>
        </li>
      </ul>
    </div>
  </SectionBand>
</template>

<script setup>
import {computed, ref} from "vue";
import SectionBand from "../shared/SectionBand.vue";
import Media from "../shared/Media.vue";
import {safeLink} from "@/utils/blocks.js";
import {useBackLinks} from "@/composables/useBackLinks.js";
const back = useBackLinks();

const props = defineProps({
  section: {type: Object, required: true},
  variant: {type: [Number, String], default: 1},
  items: {type: Array, default: null},   // resolved by the server for products/projects/posts
});
const cards = computed(() => {
  const list = Array.isArray(props.items) ? props.items : (Array.isArray(props.section.resolvedItems) ? props.section.resolvedItems : props.section.items);
  const max = Number(props.section.count) > 0 ? Number(props.section.count) : 12;
  return (Array.isArray(list) ? list : []).filter((c) => c && c.title).slice(0, max)
    .map((c) => ({...c, href: safeLink(c.link || c.path || "")}));
});
const track = ref(null);
function step(d) {
  const el = track.value;
  if (!el) return;
  el.scrollBy({left: d * el.clientWidth * 0.8, behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"});
}
</script>

<style scoped>
.s2-rail { position: relative; }
.s2-rail__head { display: flex; justify-content: flex-end; gap: .5rem; margin: -3.25rem 0 1rem; }
.s2-rail__nav { width: 44px; height: 44px; border-radius: 999px; border: 1.5px solid var(--t-line); background: var(--t-surface); color: var(--t-ink); font-size: 1.3rem; cursor: pointer; }
.s2-rail__track { list-style: none; margin: 0; padding: 0 0 .5rem; display: grid; grid-auto-flow: column; grid-auto-columns: calc((100% - 3 * var(--t-gap)) / 3.4); gap: var(--t-gap); overflow-x: auto; scroll-snap-type: x mandatory; scrollbar-width: thin; }
.s2-rail__item { scroll-snap-align: start; }
.s2-rail__card { display: flex; flex-direction: column; gap: .4rem; color: inherit; text-decoration: none; }
.s2-rail__title { margin-top: .4rem; font-family: var(--t-font-display); }
.s2-rail__text { font-size: .9rem; }
@media (max-width: 760px) { .s2-rail__track { grid-auto-columns: 72%; } .s2-rail__head { display: none; } }
</style>
